<template>
  <div>
    <v-text-field
      v-model="state.orgId"
      required
      :class="{ text__error: fieldErrors.password }"
      v-model:error-messages="fieldErrors.password"
      variant="outlined"
      density="compact"
      prepend-inner-icon="mdi-domain"
      label="Org ID"></v-text-field>
    <v-switch
      class="mt-0"
      v-model="state.useTestEmails"
      label="Use @example for all carrier emails"
      color="secondary"
      hide-details></v-switch>

    <v-expansion-panels class="my-4">
      <v-expansion-panel title="Advanced Options">
        <v-expansion-panel-text>
          <h6>Appointment Export Date Range</h6>
          <div
            class="v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single">
            <div class="input__control">
              <div
                class="v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme">
                <div class="v-field__field">
                  <VueDatePicker
                    name="start-date-picker"
                    auto-apply
                    :teleport="true"
                    :enable-time-picker="false"
                    :month-change-on-scroll="false"
                    :text-input-options="{
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    }"
                    class="date-picker"
                    v-model="startDate"></VueDatePicker>
                </div>
                <div class="v-field__outline">
                  <div class="v-field__outline__start"></div>
                  <div class="v-field__outline__notch">
                    <label class="v-label v-field-label v-field-label--floating" aria-hidden="true">
                      Appointment Export Start Date
                    </label>
                  </div>
                  <div class="v-field__outline__end"></div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="v-input v-input--density-compact v-input--dirty v-input--readonly v-text-field v-select v-select--single">
            <div class="input__control">
              <div
                class="v-field v-field--active v-field--appended v-field--dirty v-field--has-label v-field--variant-outlined v-theme--opendockTheme">
                <div class="v-field__field">
                  <VueDatePicker
                    name="end-date-picker"
                    auto-apply
                    :teleport="true"
                    :enable-time-picker="false"
                    :month-change-on-scroll="false"
                    :text-input-options="{
                      enterSubmit: true,
                      tabSubmit: true,
                      format: 'MM/dd/yyyy'
                    }"
                    class="date-picker"
                    v-model="endDate"></VueDatePicker>
                </div>
                <div class="v-field__outline">
                  <div class="v-field__outline__start"></div>
                  <div class="v-field__outline__notch">
                    <label class="v-label v-field-label v-field-label--floating" aria-hidden="true">
                      Appointment Export End Date
                    </label>
                  </div>
                  <div class="v-field__outline__end"></div>
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="d-flex justify-center justify-self-end">
      <warehouse-select-dialog
        :warehouses="state.warehouses"
        :is-dialog-visible="state.shouldShowDialog"
        @load-warehouses="loadWarehouses"
        :loading="state.loading"
        @export-data="exportData"
        @close="close"></warehouse-select-dialog>
    </div>
    <progress-dialog
      title="Exporting Data From Nova"
      :should-show-progress-dialog="progress.shouldShowProgressDialog"
      :current-step-message="progress.currentStepMessage"
      :current-perc="progress.currentPerc"></progress-dialog>
  </div>
</template>

<script lang="ts" setup>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { reactive, ref, watch } from 'vue';
import { APIs } from '@/enums/APIs';
import { getClient } from '@/services/clientService';
import type { GenericArrayOfObjects, INormalizedResponse } from '@/interfaces/api';
import type { INovaAppointment, INovaDock, INovaLoadType, INovaWarehouse } from '@/interfaces/nova';
import { exportWorkbook, type IExportedData } from '@/services/workbookService';

import {
  cleanWhiteSpace,
  flashErrorsIfExist,
  makeUniqueObjectArray,
  systemNotify
} from '@/services/util';
import { DateTime } from 'luxon';
import WarehouseSelectDialog from '@/components/dialogs/WarehouseSelectDialog.vue';
import ProgressDialog from '@/components/dialogs/ProgressDialog.vue';
import { useProgress } from '@/composables/progressBar';
import config from '@/config/config';
import { NovaApiService } from '@/services/nova/NovaApiService';

const state = reactive({
  orgId: '',
  loading: false,
  shouldShowDialog: false,
  warehouses: [] as GenericArrayOfObjects,
  useTestEmails: config.use_test_emails ? Boolean(config.use_test_emails) : true
});

const APPT_RANGE_START_DAYS_FROM_NOW = 1;
const APPT_RANGE_END_DAYS_FROM_NOW = 365;

const startDate = ref();
const endDate = ref();

const TESTING_EMAIL_DOMAIN = '@example.com';

const progress = reactive(useProgress());

// EXPORT
const exportData = async (warehousesToExport: string[]) => {
  if (!isValid()) {
    return;
  }

  const APPT_RANGE_START_LUXON = startDate.value
    ? DateTime.fromJSDate(startDate.value).startOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_START_DAYS_FROM_NOW }).startOf('day');
  const APPT_RANGE_END_LUXON = endDate.value
    ? DateTime.fromJSDate(endDate.value).endOf('day')
    : DateTime.now().plus({ days: APPT_RANGE_END_DAYS_FROM_NOW });

  state.loading = true;
  const apiService = getClient({
    api: APIs.NOVA,
    token: localStorage.getItem('access_token') as string
  }) as NovaApiService;

  const exportedData: IExportedData = {
    warehouses: [],
    loadTypes: [],
    docks: [],
    appointments: []
  };
  // GET WAREHOUSES
  progress.setDialogVisibility(true);
  progress.createInterval('Fetching Warehouses', 15);
  try {
    const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
      s: {
        id: {
          $in: warehousesToExport
        }
      }
    });
    if (flashErrorsIfExist(warehouseResponse)) {
      abortExportProcess();
      return false;
    }
    if (state.useTestEmails) {
      warehouseResponse.data = warehouseResponse.data.map(warehouse => {
        if (!cleanWhiteSpace(warehouse.email)) {
          warehouse.email = 'warehouse_placeholder';
        }

        warehouse.ccEmails = getTestEmails(warehouse.ccEmails);
        warehouse.email = getTestEmail(warehouse.email);
        return warehouse;
      });
    }

    exportedData.warehouses = warehouseResponse.data as INovaWarehouse[];
  } catch (e) {
    abortExportProcess();
    return false;
  }

  // GET DOCKS
  progress.createInterval('Fetching Docks', 40);
  try {
    const dockPromises: Promise<INormalizedResponse>[] = warehousesToExport.map(async warehouse => {
      return apiService.getDocks({
        s: {
          warehouseId: {
            $eq: warehouse
          }
        }
      });
    });

    let docks: INovaDock[] = [];
    let errors = false;

    await Promise.all(dockPromises).then(responses => {
      responses.forEach(response => {
        if (flashErrorsIfExist(response)) {
          errors = true;
          abortExportProcess();
          return false;
        }
        docks = [...docks, ...(response.data as INovaDock[])];
      });
    });

    if (errors) {
      return;
    }

    exportedData.docks = makeUniqueObjectArray(docks) as INovaDock[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    return abortExportProcess();
  }

  // GET LOAD TYPES
  await progress.zoom(40);
  progress.createInterval('Fetching Load Types', 60);
  let loadTypesArr = [] as string[];
  exportedData.docks.forEach(dock => {
    loadTypesArr = [...loadTypesArr, ...(dock.loadTypeIds as string[])];
  });
  const loadTypes = Array.from(new Set(loadTypesArr));
  try {
    const loadTypesResponse: INormalizedResponse = await apiService.getLoadTypes({
      s: {
        id: {
          $in: loadTypes
        }
      }
    });
    if (flashErrorsIfExist(loadTypesResponse)) {
      abortExportProcess();
      return false;
    }
    exportedData.loadTypes = loadTypesResponse.data as INovaLoadType[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    return abortExportProcess();
  }
  // GET APPOINTMENTS
  await progress.zoom(60);
  progress.createInterval('Fetching Appointments', 98);
  try {
    const appointmentResponse: INormalizedResponse = await apiService.getAppointments({
      s: {
        $and: [
          {
            dockId: {
              $in: exportedData.docks.map(dock => dock.id)
            }
          },
          {
            $and: [
              {
                start: {
                  $between: [APPT_RANGE_START_LUXON, APPT_RANGE_END_LUXON]
                }
              },
              {
                end: {
                  $between: [APPT_RANGE_START_LUXON, APPT_RANGE_END_LUXON]
                }
              }
            ]
          }
        ]
      },
      join: ['user', 'user.company', 'dock']
    });

    let errors = false;

    if (flashErrorsIfExist(appointmentResponse)) {
      errors = true;
    }
    if (state.useTestEmails) {
      appointmentResponse.data = appointmentResponse.data.map(appointment => {
        const newEmails = getTestEmails(appointment.ccEmails);
        appointment.ccEmails = newEmails.join(',');

        return appointment;
      });
    }

    if (errors) {
      abortExportProcess();
      return false;
    }

    exportedData.appointments = appointmentResponse.data as INovaAppointment[];
  } catch (error: any) {
    systemNotify({ message: error.message, type: 'error' });
    abortExportProcess();
    return false;
  }

  exportWorkbook(exportedData, `${APIs.NOVA} export - ${state.orgId}`);
  state.loading = false;
  state.warehouses = [];
  progress.clear();
  await progress.zoom(99, 5);
  progress.update('All done!', 100);
  setTimeout(() => {
    progress.setDialogVisibility(false);
    progress.update('', 0);
  }, 2000);
};

const getTestEmails = (emails: string[]) => {
  const newEmails: string[] = [];
  if (emails?.length > 0 && emails[0]) {
    emails.forEach((email: string) => {
      newEmails.push(getTestEmail(email));
    });
  }
  return newEmails;
};

const getTestEmail = (email: string) => {
  const emailParts = email.split('@');
  return `${emailParts[0]}${TESTING_EMAIL_DOMAIN}`;
};

const abortExportProcess = () => {
  state.loading = false;
  state.shouldShowDialog = false;
  progress.setDialogVisibility(false);
  progress.clear();
  progress.update('', 0);
};

const close = (shouldClearWarehouses = true) => {
  state.shouldShowDialog = false;
  if (shouldClearWarehouses) {
    state.warehouses = [];
  }
};

const loadWarehouses = async () => {
  const apiService = getClient({
    api: APIs.NOVA,
    token: localStorage.getItem('access_token') as string
  }) as NovaApiService;
  state.loading = true;
  const warehouseResponse: INormalizedResponse = await apiService.getWarehouses({
    s: {
      orgId: {
        $eq: state.orgId
      }
    }
  });
  state.loading = false;
  if (flashErrorsIfExist(warehouseResponse)) {
    abortExportProcess();
    return false;
  }
  state.warehouses = warehouseResponse.data;
  state.shouldShowDialog = true;
};

// VALIDATION
const fieldErrors = reactive({
  orgId: ''
});
const errorMessages = {
  orgId: 'A valid Org ID is required'
};
const isValid = () => {
  if (!state.orgId) {
    fieldErrors.orgId = errorMessages.orgId;
  }
  if (fieldErrors.orgId) {
    return false;
  }

  return true;
};

watch(
  state,
  newState => {
    if (newState.orgId) {
      fieldErrors.orgId = '';
    } else {
      fieldErrors.orgId = errorMessages.orgId;
    }
  },
  { deep: true }
);
</script>

<style lang="scss">
.input__control {
  z-index: 1;
  position: relative;
}
.v-expansion-panels {
  z-index: 100 !important;
}
[name='start-date-picker'],
[name='end-date-picker'] {
  border: none;
  padding-top: 8px;
}
</style>
